import React, { useEffect, useMemo, useState } from "react";
import {
  FaCreditCard,
  FaCheckCircle,
  FaTimesCircle,
  FaRupeeSign,
  FaSearch,
  FaSyncAlt,
  FaCopy,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { API } from "../../../config/api";
import { get } from "../../../lib/http";
import { AD } from "../../adminUi.js";

const PAGE_SIZE = 10;
const cn = (...a) => a.filter(Boolean).join(" ");

function fmtMoney(p) {
  const n = Number(p?.finalAmount ?? p?.originalAmount ?? 0);
  const cur = p?.currency || "INR";
  const sym = cur === "INR" ? "₹" : `${cur} `;
  return `${sym}${n.toLocaleString("en-IN")}`;
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function statusTone(v) {
  const s = String(v || "").toLowerCase();
  if (s === "captured" || s === "paid" || s === "success") return "green";
  if (s === "failed") return "red";
  if (s === "refunded") return "amber";
  if (s === "pending" || s === "created") return "blue";
  return "gray";
}

function StatusBadge({ value }) {
  const tone = statusTone(value);
  const map = {
    green: "bg-emerald-100 text-emerald-800 ring-emerald-200/80",
    red: "bg-rose-100 text-rose-800 ring-rose-200/80",
    amber: "bg-amber-100 text-amber-900 ring-amber-200/80",
    blue: "bg-sky-100 text-sky-800 ring-sky-200/80",
    gray: "bg-slate-100 text-slate-700 ring-slate-200/80",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset capitalize",
        map[tone]
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {value || "unknown"}
    </span>
  );
}

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch {
          /* ignore */
        }
      }}
      className={cn(
        AD.btnGhost,
        "ml-1.5 h-7 gap-1 px-2 py-0 text-[11px]",
        copied && "text-emerald-700"
      )}
      title="Copy transaction ID"
    >
      <FaCopy className="text-[10px] opacity-70" />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function KpiCard({ icon: Icon, label, value, hint, tone = "amber" }) {
  const iconBg = {
    amber: "from-amber-400 to-orange-500",
    green: "from-emerald-400 to-emerald-600",
    red: "from-rose-400 to-rose-600",
    blue: "from-sky-400 to-blue-600",
  };
  return (
    <div className={cn(AD.statCard, "flex items-start gap-3")}>
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm",
          iconBg[tone] || iconBg.amber
        )}
      >
        <Icon className="text-sm" />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="truncate text-xl font-bold text-slate-900 dark:text-white">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="animate-pulse border-t border-slate-100">
          {Array.from({ length: 7 }).map((__, j) => (
            <td key={j} className="px-4 py-3">
              <div className="h-4 w-[85%] rounded bg-slate-200" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function PaymentMobileCard({ p }) {
  return (
    <article className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-mono text-xs text-slate-500">{p.transactionId || "—"}</p>
          <p className="mt-1 font-semibold text-slate-900">{p.eventName || "—"}</p>
        </div>
        <StatusBadge value={p.liveStatus} />
      </div>
      <p className="mt-2 text-lg font-bold text-slate-900">{fmtMoney(p)}</p>
      <p className="text-sm text-slate-600">{p.userName || "—"}</p>
      <p className="text-xs text-slate-500">{p.userEmail}</p>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium capitalize">
          {p.method || "—"}
        </span>
        <span>{fmtDateTime(p.createdAt)}</span>
      </div>
    </article>
  );
}

export default function AdminPaymentList() {
  const [payments, setPayments] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
        const res = await get(API.events.getEvent, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        if (alive && res?.events) setEvents(res.events);
      } catch (err) {
        console.error("getEvents error:", err);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const fetchPayments = async (soft = false) => {
    try {
      if (soft) setRefreshing(true);
      else setLoading(true);
      setError(null);
      const token = JSON.parse(localStorage.getItem("bb_user"))?.token;
      const res = await get(
        `${API.Payment.getLivePayments}?page=${page}&limit=${PAGE_SIZE}${
          selectedEvent ? `&eventId=${selectedEvent}` : ""
        }`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        }
      );

      if (Array.isArray(res?.payments)) {
        setPayments(
          res.payments.map((p) => ({
            ...p,
            createdAt: p.createdAt || p.created_at || p.time || p.date,
          }))
        );
        setTotalPages(res.totalPages || 1);
      } else {
        setPayments([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("getLivePayments error:", err);
      setError("Unable to load payments.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedEvent]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return payments.filter((p) => {
      const statusOk =
        statusFilter === "all" ||
        String(p.liveStatus || "").toLowerCase() === statusFilter;
      if (!statusOk) return false;
      if (!term) return true;
      return [
        p?.transactionId,
        p?.userName,
        p?.userEmail,
        p?.userMobile,
        p?.eventName,
        p?.method,
        p?.liveStatus,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [payments, q, statusFilter]);

  const stats = useMemo(() => {
    const captured = filtered.filter((p) =>
      ["captured", "paid", "success"].includes(String(p.liveStatus || "").toLowerCase())
    );
    const failed = filtered.filter((p) => String(p.liveStatus || "").toLowerCase() === "failed");
    const refunded = filtered.filter((p) => String(p.liveStatus || "").toLowerCase() === "refunded");
    const volume = captured.reduce(
      (s, p) => s + Number(p.finalAmount ?? p.originalAmount ?? 0),
      0
    );
    return {
      total: filtered.length,
      captured: captured.length,
      failed: failed.length,
      refunded: refunded.length,
      volume,
    };
  }, [filtered]);

  return (
    <div className={cn(AD.formWrapWide, AD.page)}>
      <div className={AD.headerRow}>
        <div>
          <h1 className={AD.title}>Payments</h1>
          <p className={AD.subtitle}>
            Live Razorpay transactions — filter by event, status, or search.
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchPayments(true)}
          disabled={loading || refreshing}
          className={cn(AD.btnSecondary, "gap-2")}
        >
          <FaSyncAlt className={cn("text-xs", refreshing && "animate-spin")} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          icon={FaCreditCard}
          label="On this page"
          value={stats.total}
          hint={`Page ${page} of ${totalPages}`}
          tone="blue"
        />
        <KpiCard
          icon={FaRupeeSign}
          label="Captured volume"
          value={`₹${stats.volume.toLocaleString("en-IN")}`}
          hint={`${stats.captured} successful txn`}
          tone="green"
        />
        <KpiCard icon={FaCheckCircle} label="Captured" value={stats.captured} tone="green" />
        <KpiCard
          icon={FaTimesCircle}
          label="Failed / Refunded"
          value={`${stats.failed} / ${stats.refunded}`}
          tone={stats.failed > 0 ? "red" : "amber"}
        />
      </div>

      <div className={AD.filterCard}>
        <div className="grid gap-4 md:grid-cols-12">
          <div className="md:col-span-5">
            <label className={AD.fieldLabel}>Search</label>
            <div className="relative mt-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Txn ID, user, email, event, method…"
                className={cn(AD.inputField, "pl-10")}
              />
            </div>
          </div>

          <div className="md:col-span-4">
            <label className={AD.fieldLabel}>Event</label>
            <select
              value={selectedEvent}
              onChange={(e) => {
                setSelectedEvent(e.target.value);
                setPage(1);
              }}
              className={cn(AD.select, "mt-1")}
            >
              <option value="">All events</option>
              {events.map((ev) => (
                <option key={ev._id} value={ev._id}>
                  {ev.name}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <label className={AD.fieldLabel}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={cn(AD.select, "mt-1")}
            >
              <option value="all">All statuses</option>
              <option value="captured">Captured</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-200" />
          ))
        ) : error ? (
          <div className={AD.emptyState}>{error}</div>
        ) : filtered.length === 0 ? (
          <div className={AD.emptyState}>
            <FaCreditCard className="mx-auto mb-2 text-2xl text-amber-500" />
            <p className="font-semibold">No payments found</p>
            <p className="mt-1 text-sm">Try another filter or search term.</p>
          </div>
        ) : (
          filtered.map((p) => <PaymentMobileCard key={p._id} p={p} />)
        )}
      </div>

      <div className={cn(AD.tableShell, "hidden md:block")}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className={AD.thead}>
              <tr>
                <th className="px-4 py-3 text-left">Txn ID</th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Event</th>
                <th className="px-4 py-3 text-left">Amount</th>
                <th className="px-4 py-3 text-left">Method</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows />
              ) : error ? (
                <tr>
                  <td colSpan={7} className="p-10 text-center font-medium text-rose-600">
                    {error}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className={AD.emptyState}>
                      <FaCreditCard className="mx-auto mb-2 text-2xl text-amber-500" />
                      <p className="font-semibold">No payments found</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map((p) => (
                  <tr
                    key={p._id}
                    className="transition hover:bg-amber-50/40 dark:hover:bg-gray-800/40"
                  >
                    <td className="px-4 py-3">
                      <div className="flex max-w-[200px] items-center">
                        <span className="truncate font-mono text-xs font-medium text-slate-800">
                          {p.transactionId || "—"}
                        </span>
                        <CopyBtn text={p.transactionId} />
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{p.userName || "—"}</p>
                      <p className="text-xs text-slate-500">{p.userEmail || ""}</p>
                      {p.userMobile && (
                        <p className="text-xs text-slate-400">{p.userMobile}</p>
                      )}
                    </td>
                    <td className="max-w-[180px] px-4 py-3">
                      <p className="truncate font-medium text-slate-900">{p.eventName || "—"}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-900">{fmtMoney(p)}</p>
                      {Number(p.originalAmount) > Number(p.finalAmount) && (
                        <p className="text-[11px] font-medium text-emerald-600">Discount applied</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold capitalize text-slate-700">
                        {p.method || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge value={p.liveStatus} />
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                      {fmtDateTime(p.createdAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && !error && filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/80 px-4 py-3 text-xs dark:border-gray-800 dark:bg-gray-900/50">
            <span className="text-slate-600">
              Showing <strong>{filtered.length}</strong> of <strong>{payments.length}</strong> on
              this page
            </span>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-800">
                Captured {stats.captured}
              </span>
              <span className="rounded-full bg-rose-100 px-2.5 py-1 font-semibold text-rose-800">
                Failed {stats.failed}
              </span>
              <span className="rounded-full bg-amber-100 px-2.5 py-1 font-semibold text-amber-900">
                Refunded {stats.refunded}
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Page <strong>{page}</strong> of <strong>{totalPages}</strong>
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1 || loading}
            className={cn(AD.btnSecondary, "gap-1", page === 1 && "opacity-50")}
          >
            <FaChevronLeft className="text-xs" /> Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages || loading}
            className={cn(AD.btnSecondary, "gap-1", page === totalPages && "opacity-50")}
          >
            Next <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}
